/**
 * Real API module. One exported function per endpoint in the backend contract
 * (docs/uw-workbench/backend-architecture.md Section 2); every response is
 * validated with Zod before it reaches a store. Endpoints the backend does not
 * define yet throw NotImplementedError so callers fail loudly.
 */
import type {
  AddAssignedRatingsRequest,
  AddAssignedRatingsResult,
  AssignedRatingPatch,
  BuildLookupRow,
  CatalogSearchFilter,
  CatalogSearchResult,
  SmartSearchResult,
  CensusImportPreview,
  ChangeReason,
  ConditionDraft,
  ConditionGuidance,
  ConditionPatch,
  ConditionSearchHit,
  CreateGroupRequest,
  CreateGroupResponse,
  CurrentUser,
  DebitModificationDraft,
  ExpectedDebitsTable,
  ExpectedDebitsTableDraft,
  MugsManualApplyResult,
  MugsManualChange,
  MugsManualChangeDecision,
  MugsManualChangeKind,
  MugsManualChangeSummary,
  MugsManualRelease,
  MugsManualUploadDraft,
  GroupDetail,
  GroupDraft,
  GroupHistorySummary,
  GroupPatch,
  GroupSearchQuery,
  GroupSummary,
  MedicalCondition,
  MemberDraft,
  MemberPatch,
  MemberFlagsPatch,
  FlagChangeResult,
  ModificationReportRow,
  ModificationReportQuery,
  OpportunitySummary,
  PagedResult,
  PriorYearRating,
  Rating,
  RatingDraft,
  RatingPatch,
  RatingWithEffectiveDebits,
  ReopenGroupResult,
  RmmPreview,
  RmmPreviewRequest,
  SalesCensus,
  RestoreVersionRequest,
  RestoreVersionResult,
  SendToSalesResult,
  ToggleMemberExcludedResult,
  WorkbenchUser,
} from "../types";
import { NotImplementedError } from "../types";
import { z } from "zod";
import { apiFetch, buildQuery } from "./apiFetch";
import {
  AddAssignedRatingsResultSchema,
  BuildLookupRowSchema,
  CatalogSearchResultSchema,
  SmartSearchResultSchema,
  ConditionGuidanceSchema,
  ConditionSearchHitSchema,
  CreateGroupResponseSchema,
  CurrentUserSchema,
  ExpectedDebitsTableSchema,
  FlagChangeResultSchema,
  MugsManualApplyResultSchema,
  MugsManualChangeSchema,
  MugsManualChangeSummarySchema,
  MugsManualReleaseSchema,
  GroupDetailSchema,
  GroupHistorySummarySchema,
  GroupSummarySchema,
  MedicalConditionSchema,
  ModificationReportRowSchema,
  OpportunitySummarySchema,
  PriorYearRatingSchema,
  RatingSchema,
  RatingWithEffectiveDebitsSchema,
  ReopenGroupResultSchema,
  RmmPreviewSchema,
  SalesCensusSchema,
  RestoreVersionResultSchema,
  SendToSalesResultSchema,
  ToggleMemberExcludedResultSchema,
  WorkbenchUserSchema,
  pagedResultSchema,
} from "../validation/schemas";

async function getJson<T>(path: string, schema: z.ZodType<T>): Promise<T> {
  return schema.parse(await apiFetch<unknown>(path));
}

async function sendJson<T>(method: "POST" | "PATCH" | "PUT" | "DELETE", path: string, schema: z.ZodType<T>, body?: unknown): Promise<T> {
  return schema.parse(await apiFetch<unknown>(path, { method, body: body === undefined ? undefined : JSON.stringify(body) }));
}

export async function getCurrentUser(): Promise<CurrentUser> {
  return getJson("/me", CurrentUserSchema);
}

export async function listUsers(): Promise<WorkbenchUser[]> {
  return getJson("/users", z.array(WorkbenchUserSchema));
}

export async function listQuoteOwners(): Promise<string[]> {
  return getJson("/opportunities/quote-owners", z.array(z.string()));
}

export async function getRecentGroups(): Promise<GroupSummary[]> {
  return getJson("/groups/recent", z.array(GroupSummarySchema));
}

export async function searchGroups(query: GroupSearchQuery): Promise<PagedResult<GroupSummary>> {
  return getJson(`/groups${buildQuery({ ...query })}`, pagedResultSchema(GroupSummarySchema));
}

export async function getGroup(groupId: string): Promise<GroupDetail> {
  return getJson(`/groups/${encodeURIComponent(groupId)}`, GroupDetailSchema);
}

export async function searchOpportunities(search: string): Promise<OpportunitySummary[]> {
  return getJson(`/opportunities${buildQuery({ search })}`, z.array(OpportunitySummarySchema));
}

export async function createGroup(draft: GroupDraft): Promise<GroupDetail> {
  if (draft.copyOfGroupId) {
    const { copyOfGroupId, ...patch } = draft;
    return sendJson("POST", `/groups/${encodeURIComponent(copyOfGroupId)}/copy`, GroupDetailSchema, patch);
  }
  return sendJson("POST", "/groups", GroupDetailSchema, { ...draft, source: "Manual" });
}

export async function copyGroup(groupId: string): Promise<GroupDetail> {
  return sendJson("POST", `/groups/${encodeURIComponent(groupId)}/copy`, GroupDetailSchema);
}

export async function updateGroup(groupId: string, patch: GroupPatch): Promise<GroupDetail> {
  return sendJson("PATCH", `/groups/${encodeURIComponent(groupId)}`, GroupDetailSchema, patch);
}

export async function reopenGroup(groupId: string): Promise<ReopenGroupResult> {
  return sendJson("POST", `/groups/${encodeURIComponent(groupId)}/reopen`, ReopenGroupResultSchema);
}

export async function restoreVersion(groupId: string, request: RestoreVersionRequest): Promise<RestoreVersionResult> {
  return sendJson("POST", `/groups/${encodeURIComponent(groupId)}/restore`, RestoreVersionResultSchema, request);
}

export async function deleteGroup(groupId: string): Promise<void> {
  await apiFetch<undefined>(`/groups/${encodeURIComponent(groupId)}`, { method: "DELETE" });
}

export async function getGroupHistory(groupId: string): Promise<GroupHistorySummary> {
  return getJson(`/groups/${encodeURIComponent(groupId)}/history`, GroupHistorySummarySchema);
}

export async function addMember(groupId: string, draft: MemberDraft): Promise<GroupDetail> {
  return sendJson("POST", `/groups/${encodeURIComponent(groupId)}/members`, GroupDetailSchema, draft);
}

export async function updateMember(groupId: string, memberId: string, patch: MemberPatch): Promise<GroupDetail> {
  return sendJson("PATCH", `/groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(memberId)}`, GroupDetailSchema, patch);
}

export async function toggleMemberExcluded(groupId: string, memberId: string, excluded: boolean): Promise<ToggleMemberExcludedResult> {
  return sendJson("PATCH", `/groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(memberId)}`, ToggleMemberExcludedResultSchema, { isExcluded: excluded });
}

export async function removeMember(groupId: string, memberId: string): Promise<GroupDetail> {
  return sendJson("DELETE", `/groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(memberId)}`, GroupDetailSchema);
}

export async function addAssignedRatings(groupId: string, memberId: string, request: AddAssignedRatingsRequest): Promise<AddAssignedRatingsResult> {
  return sendJson("POST", `/groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(memberId)}/ratings`, AddAssignedRatingsResultSchema, request);
}

export async function updateAssignedRating(groupId: string, memberId: string, assignedRatingId: string, patch: AssignedRatingPatch): Promise<GroupDetail> {
  return sendJson("PATCH", `/groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(memberId)}/ratings/${encodeURIComponent(assignedRatingId)}`, GroupDetailSchema, patch);
}

/** IRIX Source alone: the API saves it without the engine or the group graph and answers with the group status only. */
export async function setAssignedRatingFlags(groupId: string, memberId: string, assignedRatingId: string, flags: { irixSource: boolean }): Promise<FlagChangeResult> {
  return sendJson("PATCH", `/groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(memberId)}/ratings/${encodeURIComponent(assignedRatingId)}/flags`, FlagChangeResultSchema, flags);
}

/** IRIX Checked and Signed Auth: the same light save on the member. */
export async function setMemberFlags(groupId: string, memberId: string, flags: MemberFlagsPatch): Promise<FlagChangeResult> {
  return sendJson("PATCH", `/groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(memberId)}/flags`, FlagChangeResultSchema, flags);
}

export async function removeAssignedRating(groupId: string, memberId: string, assignedRatingId: string, changeReason: ChangeReason | null = null, changeExplanation: string | null = null): Promise<GroupDetail> {
  return sendJson("DELETE", `/groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(memberId)}/ratings/${encodeURIComponent(assignedRatingId)}`, GroupDetailSchema, { changeReason, changeExplanation });
}

export async function sendToSales(groupId: string): Promise<SendToSalesResult> {
  return sendJson("POST", `/groups/${encodeURIComponent(groupId)}/send-to-sales`, SendToSalesResultSchema);
}

export async function getSentCensus(groupId: string): Promise<SalesCensus | null> {
  return getJson(`/groups/${encodeURIComponent(groupId)}/sent-census`, SalesCensusSchema.nullable());
}

/** Also the endpoint Salesforce calls for the Sales-side what-if, so the RMM formula lives in one place. */
export async function previewAdjustedRmm(groupId: string, request: RmmPreviewRequest): Promise<RmmPreview> {
  return sendJson("POST", `/groups/${encodeURIComponent(groupId)}/rmm-preview`, RmmPreviewSchema, request);
}

export async function getMemberPriorYearRatings(groupId: string, memberId: string): Promise<PriorYearRating[]> {
  return getJson(`/groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(memberId)}/prior-year-ratings`, z.array(PriorYearRatingSchema));
}

export async function importCensusPreview(_file: File): Promise<CensusImportPreview> {
  throw new NotImplementedError("POST /groups/import-census/preview");
}

export async function publishSkynetGroup(request: CreateGroupRequest): Promise<CreateGroupResponse> {
  return sendJson("POST", "/groups", CreateGroupResponseSchema, request);
}

/** The whole catalog (about 8 MB for 26k ratings), loaded once per session; it gets more time than an ordinary call. */
export async function getBuildTable(): Promise<BuildLookupRow[]> {
  return getJson("/catalog/build-table", z.array(BuildLookupRowSchema));
}

export async function getCondition(conditionId: number): Promise<MedicalCondition> {
  return getJson(`/catalog/conditions/${conditionId}`, MedicalConditionSchema);
}

export async function getMostUsedConditions(take: number): Promise<ConditionSearchHit[]> {
  return getJson(`/catalog/conditions/most-used${buildQuery({ take })}`, z.array(ConditionSearchHitSchema));
}

export async function listConditions(includeInactive = false): Promise<ConditionSearchHit[]> {
  return getJson(`/catalog/conditions${buildQuery({ includeInactive })}`, z.array(ConditionSearchHitSchema));
}

export async function searchCatalog(query: string, filter: CatalogSearchFilter): Promise<CatalogSearchResult> {
  return getJson(`/catalog/conditions${buildQuery({ search: query, filter })}`, CatalogSearchResultSchema);
}

/** Suggestions through Skynet's embedding and search index; slower than the name search, so the picker calls it on a pause. */
export async function smartSearchCatalog(query: string, filter: CatalogSearchFilter): Promise<SmartSearchResult> {
  return getJson(`/catalog/conditions/smart${buildQuery({ search: query, filter })}`, SmartSearchResultSchema);
}

export async function getConditionRatings(conditionId: number, includeInactive = false): Promise<RatingWithEffectiveDebits[]> {
  return getJson(`/catalog/conditions/${conditionId}/ratings${buildQuery({ includeInactive: includeInactive || undefined })}`, z.array(RatingWithEffectiveDebitsSchema));
}

export async function getConditionGuidance(conditionId: number): Promise<ConditionGuidance> {
  return getJson(`/catalog/conditions/${conditionId}/guidance`, ConditionGuidanceSchema);
}

export async function listExpectedDebitTables(): Promise<ExpectedDebitsTable[]> {
  return getJson("/expected-debits-tables", z.array(ExpectedDebitsTableSchema));
}

export async function getExpectedDebitTable(tableId: string): Promise<ExpectedDebitsTable> {
  return getJson(`/expected-debits-tables/${encodeURIComponent(tableId)}`, ExpectedDebitsTableSchema);
}

export async function createExpectedDebitTable(draft: ExpectedDebitsTableDraft): Promise<ExpectedDebitsTable> {
  return sendJson("POST", "/expected-debits-tables", ExpectedDebitsTableSchema, draft);
}

export async function activateExpectedDebitTable(tableId: string): Promise<ExpectedDebitsTable[]> {
  return sendJson("POST", `/expected-debits-tables/${encodeURIComponent(tableId)}/activate`, z.array(ExpectedDebitsTableSchema));
}

export async function createCondition(draft: ConditionDraft): Promise<MedicalCondition> {
  return sendJson("POST", "/catalog/conditions", MedicalConditionSchema, draft);
}

export async function updateCondition(conditionId: number, patch: ConditionPatch): Promise<MedicalCondition> {
  return sendJson("PATCH", `/catalog/conditions/${conditionId}`, MedicalConditionSchema, patch);
}

export async function deleteCondition(conditionId: number): Promise<void> {
  await apiFetch<undefined>(`/catalog/conditions/${conditionId}`, { method: "DELETE" });
}

export async function updateRating(ratingId: number, patch: RatingPatch): Promise<RatingWithEffectiveDebits> {
  return sendJson("PATCH", `/catalog/ratings/${ratingId}`, RatingWithEffectiveDebitsSchema, patch);
}

export async function deleteRating(ratingId: number): Promise<void> {
  await apiFetch<undefined>(`/catalog/ratings/${ratingId}`, { method: "DELETE" });
}

export async function createRating(conditionId: number, draft: RatingDraft): Promise<Rating> {
  return sendJson("POST", `/catalog/conditions/${conditionId}/ratings`, RatingSchema, draft);
}

export async function saveDebitModification(ratingId: number, draft: DebitModificationDraft): Promise<RatingWithEffectiveDebits> {
  return sendJson("PUT", `/catalog/ratings/${ratingId}/modification`, RatingWithEffectiveDebitsSchema, draft);
}

export async function listModifications(query: ModificationReportQuery = {}): Promise<PagedResult<ModificationReportRow>> {
  return getJson(`/catalog/modifications${buildQuery({ ...query })}`, pagedResultSchema(ModificationReportRowSchema));
}

export async function listMugsManuals(): Promise<MugsManualRelease[]> {
  return getJson("/catalog/mugs-manuals", z.array(MugsManualReleaseSchema));
}

/** Stages a Milliman release: stores the two files, runs the matcher, and returns the change counts. Nothing is applied. */
export async function stageMugsManual(draft: MugsManualUploadDraft): Promise<MugsManualChangeSummary> {
  const form = new FormData();
  form.append("name", draft.name);
  if (draft.effectiveFrom) form.append("effectiveFrom", draft.effectiveFrom);
  if (draft.notes) form.append("notes", draft.notes);
  form.append("conditionFile", draft.conditionFile);
  form.append("ratingFile", draft.ratingFile);
  return MugsManualChangeSummarySchema.parse(await apiFetch<unknown>("/catalog/mugs-manuals", { method: "POST", body: form, timeoutMs: 600_000 }));
}

export async function getMugsManualSummary(mugsManualId: string): Promise<MugsManualChangeSummary> {
  return getJson(`/catalog/mugs-manuals/${encodeURIComponent(mugsManualId)}`, MugsManualChangeSummarySchema);
}

export async function listMugsManualChanges(mugsManualId: string, kind: MugsManualChangeKind | null): Promise<MugsManualChange[]> {
  return getJson(`/catalog/mugs-manuals/${encodeURIComponent(mugsManualId)}/changes${buildQuery({ kind })}`, z.array(MugsManualChangeSchema));
}

export async function decideMugsManualChange(mugsManualId: string, changeId: string, decision: MugsManualChangeDecision): Promise<void> {
  await apiFetch<void>(`/catalog/mugs-manuals/${encodeURIComponent(mugsManualId)}/changes/${encodeURIComponent(changeId)}`, { method: "PATCH", body: JSON.stringify({ decision }) });
}

export async function applyMugsManual(mugsManualId: string): Promise<MugsManualApplyResult> {
  return MugsManualApplyResultSchema.parse(await apiFetch<unknown>(`/catalog/mugs-manuals/${encodeURIComponent(mugsManualId)}/apply`, { method: "POST", timeoutMs: 600_000 }));
}

export async function discardMugsManual(mugsManualId: string): Promise<void> {
  await apiFetch<void>(`/catalog/mugs-manuals/${encodeURIComponent(mugsManualId)}/discard`, { method: "POST" });
}
